import { useEffect, useState } from 'react'
import {
  List,
  SimpleForm,
  TextInput,
  Datagrid,
  Edit,
  Create,
  TextField,
  ReferenceInput,
  NumberInput,
  NumberField,
  SelectInput,
  required,
  SimpleShowLayout,
  FormDataConsumer,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectArrayInput,
  BooleanInput,
  BooleanField,
  ArrayField,
  useNotify,
  useRefresh,
  useDataProvider,
  useRedirect,
  SaveButton,
  Toolbar,
  Form,
  useRecordContext,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { RichTextInput } from 'ra-input-rich-text'
import { ExtraPagination } from './utils'
import { Button } from '@material-ui/core'
import { useLocation, useParams } from 'react-router'
import { ProcessType } from './components/process/ProcessType'
import { makeStyles } from '@material-ui/core/styles'

const CreateState = () => {
  const record = useRecordContext()

  return (
    <Create resource="states" basePath="/" title=" ">
      <SimpleForm
        defaultValues={{ process_id: record.id }}
        redirect={false}
        variant="outlined"
      >
        <NumberInput source="numero" label="Numero" validate={[required()]} />
        <TextInput source="step_name" label="Name" validate={[required()]} />
      </SimpleForm>
    </Create>
  )
}

//NOTE: we had an issue with deletion. When we performed deletions in the
//various Edit forms, then a refresh happened and React-Admin tried to load the deleted resource
//which caused a redirect.
//This was caused by the Edit components, which probably used some cached data that was not updated by the refresh action.
//It seems RA refresh does not reach all parts of RA when we use Edit components inlined in other top-level components.
//So, I had to make the inlined Edit components without RA Edit, since RA Edit fetches the resource, which, in our case, may have been deleted.
//So I use only SimpleForm, with a custom save function, and a custom delete button.
const EditGeneric = (props) => {
  const dataProvider = useDataProvider()
  const refresh = useRefresh()
  const notify = useNotify()
  const record = useRecordContext(props)

  const save = (data) => {
    dataProvider
      .update(props.res, { id: record.id, data })
      .then(() => {
        refresh()
      })
      .catch((error) => {
        notify(error.message, 'warning')
      })
  }

  const del = () => {
    dataProvider
      .delete(props.res, { id: record.id })
      .then(() => {
        refresh()
      })
      .catch((error) => {
        notify(error.message, 'warning')
      })
  }

  return (
    <div>
      {props.children !== undefined && props.res ? (
        <SimpleForm
          defaultValues={record}
          redirect={false}
          variant="outlined"
          onSubmit={save}
        >
          {props.children}
        </SimpleForm>
      ) : (
        <div />
      )}
      <Button onClick={del}>Effacer</Button>
    </div>
  )
}

const EditState = (props) => {
  return (
    <EditGeneric {...props} res={'states'}>
      <NumberInput source="numero" label="Numero" />
    </EditGeneric>
  )
}

const EditField = (props) => {
  return (
    <EditGeneric {...props} res={'ticket_fields'}>
      <TextInput source="label" label="Label" />
      <TextInput source="source" label="Colonne BDD" />
      <BooleanInput
        source="ext_published"
        label="Dispo pour self-ticketing externe"
      />
      <BooleanInput source="pac_visible" label="Visible dans le PAC" />
      <BooleanInput source="required" label="Obligatoire" />
      <TextInput source="filter" label="Filtre (JSON)" />
    </EditGeneric>
  )
}

const EditRole = (props) => {
  return (
    <EditGeneric {...props} res={'roles'}>
      <TextInput source="name" label="Name" validate={[required()]} />
      <BooleanInput
        source="send_notifs"
        label="Send Notifications?"
        validate={[required()]}
      />
      <BooleanInput
        source="show_file_with_context"
        label="Show file with context?"
      />
      <TextInput
        multiline
        rows={6}
        source="notif_text"
        label="State change notification email text (variables: {ticket_id}, {ticket_libelle}, {ticket_url}, {copro_name}"
        validate={[required()]}
      />
      <TextInput
        multiline
        rows={6}
        source="notif_text_sms"
        label="State change notification sms text (variables: {ticket_id}, {ticket_libelle}, {ticket_url}, {copro_name}"
        validate={[required()]}
      />
    </EditGeneric>
  )
}

const EditTransition = (props) => {
  const record = useRecordContext(props)
  record.input_columns = record.input_columns?.map((r) => r?.id)
  record.view_columns = record.view_columns?.map((r) => r?.id)

  return (
    <EditGeneric {...props} res={'state_transitions'}>
      <ReferenceArrayInput
        label="Input Fields"
        source="input_columns"
        reference="ticket_fieldsnorole"
        basePath="/"
      >
        <SelectArrayInput optionText="label" translateChoice={false} />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        label="View Fields"
        source="view_columns"
        reference="ticket_fields"
        basePath="/"
      >
        <SelectArrayInput optionText="label" translateChoice={false} />
      </ReferenceArrayInput>
      <BooleanInput
        label="Peut passer à l'étape suivante"
        source="can_forward"
      />
      <TextInput label="Instructions" source="description" />
      <TextInput label="State button label" source="state_button_label" />
    </EditGeneric>
  )
}

const CreateField = () => {
  const record = useRecordContext()

  return (
    <Create resource="ticket_fields" basePath="/" title=" ">
      <SimpleForm
        defaultValues={{ process_id: record.id }}
        redirect={false}
        variant="outlined"
      >
        <ReferenceInput
          label="Type"
          source="type"
          reference="ticket_field_types"
          validate={[required()]}
        >
          <SelectInput optionText="name" translateChoice={false} />
        </ReferenceInput>
        <TextInput source="label" label="Label" validate={[required()]} />
        <TextInput
          source="source"
          label="Colonne BDD"
          validate={[required()]}
        />
        <BooleanInput
          source="ext_published"
          label="Dispo pour self-ticketing externe"
        />
        <BooleanInput source="pac_visible" label="Visible dans le PAC" />
      </SimpleForm>
    </Create>
  )
}
const CreateRole = (props) => {
  const record = useRecordContext()

  return (
    <Create resource="roles" basePath="/" title=" ">
      <SimpleForm
        defaultValues={{
          process_id: record.id,
        }}
        redirect={false}
        variant="outlined"
      >
        {/* <FormDataConsumer>
          {({ formData, ...rest }) => {
            window.context["context-process"] = formData.process_id;
            return null;
          }}
        </FormDataConsumer> */}
        <TextInput source="name" label="Name" validate={[required()]} />
        <ReferenceInput
          label="Contact field"
          source="contact_id"
          reference="ticket_fieldscontact"
          validate={[required()]}
        >
          <SelectInput optionText="label" translateChoice={false} />
        </ReferenceInput>
        <BooleanInput
          source="send_notifs"
          label="Send Notifications?"
          validate={[required()]}
        />
        <BooleanInput
          source="show_file_with_context"
          label="Show file with context?"
        />
        <TextInput
          multiline
          rows={6}
          source="notif_text"
          label="State change notification email text (variables: {ticket_id}, {ticket_libelle}, {ticket_url}"
          validate={[required()]}
        />
        <TextInput
          multiline
          rows={6}
          source="notif_text_sms"
          label="State change notification sms text (variables: {ticket_id}, {ticket_libelle}, {ticket_url}"
          validate={[required()]}
        />
      </SimpleForm>
    </Create>
  )
}

const CreateTransition = (props) => {
  const record = useRecordContext()

  return (
    <Create resource="state_transitions" basePath="/" title=" ">
      <SimpleForm
        defaultValues={{ process_id: record.id }}
        redirect={false}
        variant="outlined"
      >
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            // window.context["context-process"] = formData.process_id;
            return (
              <div>
                <ReferenceInput
                  label="Role"
                  source="role_id"
                  reference="roles"
                  validate={[required()]}
                >
                  <SelectInput optionText="name" translateChoice={false} />
                </ReferenceInput>
                <ReferenceInput
                  label="Start state"
                  source="start_state_id"
                  reference="states"
                  validate={[required()]}
                >
                  <SelectInput optionText="step_name" translateChoice={false} />
                </ReferenceInput>
                <ReferenceArrayInput
                  label="Input Fields"
                  source="input_columns"
                  reference="ticket_fieldsnorole"
                  basePath="/"
                >
                  <SelectArrayInput
                    optionText="label"
                    translateChoice={false}
                  />
                </ReferenceArrayInput>
                <ReferenceArrayInput
                  label="View Fields"
                  source="view_columns"
                  reference="ticket_fields"
                  basePath="/"
                >
                  <SelectArrayInput
                    optionText="label"
                    translateChoice={false}
                  />
                </ReferenceArrayInput>
                <BooleanInput
                  label="Peut passer à l'étape suivante"
                  source="can_forward"
                />
                <TextInput label="Instructions" source="description" />
                <TextInput
                  label="State button label"
                  source="state_button_label"
                />
              </div>
            )
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  )
}
const CreateProcessType = (props) => {
  const record = useRecordContext(props)
  const { classes } = props
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()

  const onSuccess = () => {
    notify('Changements sauvegardés avec succès', 'success')
    redirect('/process')
    refresh()
  }

  return (
    <Edit
      resource="processes_types"
      mutationOptions={{ onSuccess }}
      mutationMode="pessimistic"
    >
      <SimpleForm
        className={classes.spacingPadding}
        toolbar={false}
        defaultValues={{ process_id: record?.id }}
        redirect={false}
      >
        <ProcessType source="types" />
        <PostSaveButton />
      </SimpleForm>
    </Edit>
  )
}

const TextArrayField = ({ source }) => {
  const record = useRecordContext()

  return (
    <>
      {record[source]?.map((item) => (
        <div>
          <span>{item?.label}</span>
          <br />
        </div>
      ))}
    </>
  )
}

export const ProcessEdit = (props) => {
  const { id: process_id } = useParams()
  window.context['context-process'] = process_id
  const useStyles = makeStyles({
    spacingPadding: {
      padding: '20px',
    },
  })
  const classes = useStyles()

  return (
    <>
      <Edit
        title=" "
        toolbar={
          <Toolbar {...props}>
            <SaveButton {...props} />
          </Toolbar>
        }
      >
        <SimpleForm redirect={false} variant="outlined">
          <TextInput source="name" label="Nom" />
          <TextInput
            source="description"
            label="Description (pour l'espace unique)"
          />
          <TextInput
            source="tag"
            label="Tag Espaces Gestionnaire/Process owner"
            validate={[required()]}
          />
          <NumberInput source="deadline" label="Deadline (Now + X days)" />
          <BooleanInput
            source="is_copro_summary"
            label="Afficher dans plan action copro (is_copro_summary)"
            disabled
          />
          <BooleanInput
            source="published"
            label="Dispo pour création tickets (published)"
            disabled
          />
          <BooleanInput
            source="ext_published"
            label="Dispo pour self-ticketing externe (ext_published)"
            disabled
          />
          <BooleanInput
            source="isPrivate"
            label="Process privé (isPrivate)"
            disabled
          />
          <TextInput
            source="ext_description"
            label="Description (pour le self-ticketing)"
          />
          <TextInput source="color" label="Couleur (hexa)" />
          <BooleanInput
            source="has_invoice"
            label="Créa auto de child tickets de facturation?"
          />
          <TextInput
            source="qualification_typeform_id"
            label="Qualification typeform id"
          />
          <ReferenceArrayInput source="owners" reference="users" basePath="/">
            <AutocompleteArrayInput optionText="email" label="Responsables" />
          </ReferenceArrayInput>

          <BooleanInput rows={6} source="notify_janitor_on_ticket_creation" />
          <RichTextInput
            multiline
            rows={12}
            source="notify_janitor_on_ticket_creation_template"
          />
          <BooleanInput rows={6} source="notify_cs_on_ticket_creation" />
          <RichTextInput
            multiline
            rows={12}
            source="notify_cs_on_ticket_creation_template"
          />
        </SimpleForm>
      </Edit>
      <CreateProcessType {...props} classes={classes} />
      <Edit {...props}>
        <SimpleShowLayout>
          <TextField source="name" label="Nom" />
          <TextField source="description" label="Description" />
          <ArrayField label="States" source="states">
            <Datagrid>
              <NumberField source="numero" label="Numero" />
              <TextField source="step_name" label="States" />
              <EditState {...props} process_id={process_id} />
            </Datagrid>
          </ArrayField>
          <CreateState {...props} />
          <ArrayField label="Fields" source="ticket_fields">
            <Datagrid>
              <TextField source="type" label="Type" />
              <TextField source="label" label="Label" />
              <TextField source="source" label="Colonne BDD" />
              <EditField {...props} process_id={process_id} />
            </Datagrid>
          </ArrayField>
          <CreateField {...props} />
          <ArrayField label="Roles" source="roles">
            <Datagrid>
              <TextField source="name" label="Roles" />
              <TextField source="label" name="Contact Field" />
              <EditRole {...props} process_id={process_id} />
            </Datagrid>
          </ArrayField>
          <div>
            Créez un rôle 'hs' pour les personnes Manda (Ex-Hello Syndic)
          </div>
          <CreateRole {...props} />
          <ArrayField label="States" source="states">
            <Datagrid>
              <NumberField source="numero" label="Numero" />
              <TextField source="step_name" label="States" />
              <ArrayField label="State Transitions" source="state_transitions">
                <Datagrid>
                  <TextField label="Role" source="name" />
                  <TextField label="Start State" source="step_name" />
                  <TextArrayField label="Input fields" source="input_columns" />
                  <TextArrayField label="View fields" source="view_columns" />
                  <BooleanField
                    label="Peut passer à l'étape suivante"
                    source="can_forward"
                  />
                  <TextField label="Instructions " source="description" />
                  <TextField
                    label="State button label"
                    source="state_button_label"
                  />
                  <EditTransition {...props} process_id={process_id} />
                </Datagrid>
              </ArrayField>
            </Datagrid>
          </ArrayField>
          <CreateTransition {...props} />
        </SimpleShowLayout>
      </Edit>
    </>
  )
}

function PostSaveButton(props) {
  const record = useRecordContext(props)
  const { watch } = useFormContext()
  const watchedTypes = watch('types')
  const [pristine, setPristine] = useState(false)

  useEffect(() => {
    if (arraysEqual(record?.types, watchedTypes)) {
      setPristine(false)
    } else {
      setPristine(true)
    }
  }, [watchedTypes])

  return <SaveButton alwaysEnable={pristine} disableElevation />
}

export const ProcessList = () => (
  <List exporter={false} pagination={<ExtraPagination />}>
    <Datagrid bulkActionButtons={false} rowClick="edit">
      <TextField source="name" label="Nom" />
      <TextField source="description" label="Description" />
      <TextField source="tag" label="Tag" />
    </Datagrid>
  </List>
)

export const ProcessCreate = (props) => {
  return (
    <Create {...props}>
      <SimpleForm variant="outlined">
        <ReferenceInput
          label="Créer à partir de"
          source="from_process"
          reference="process"
        >
          <SelectInput optionText="name" translateChoice={false} />
        </ReferenceInput>
        <TextInput source="name" label="Name" validate={[required()]} />
        <TextInput
          source="description"
          label="Description"
          validate={[required()]}
        />
        <TextInput
          source="tag"
          label="Tag Espaces Gestionnaire/Process owner"
          validate={[required()]}
        />
        <BooleanInput
          source="is_copro_summary"
          label="Afficher dans plan action copro (is_copro_summary)"
          disabled
        />
        <NumberInput
          source="deadline"
          label="Deadline (Now + X days)"
          validate={[required()]}
        />
        <BooleanInput
          source="published"
          label="Dispo pour création tickets (published)"
          disabled
        />
        <BooleanInput
          source="ext_published"
          label="Dispo pour self-ticketing externe (ext_published)"
          disabled
        />
        <BooleanInput source="isPrivate" label="Process privé" disabled />
        <TextInput
          source="ext_description"
          label="Description (pour le self-ticketing)"
        />
        <TextInput source="color" label="Couleur (hexa)" />
        <BooleanInput
          source="has_invoice"
          label="Créa auto de child tickets de facturation?"
        />
        <TextInput
          source="qualification_typeform_id"
          label="Qualification typeform id"
        />
        <BooleanInput rows={6} source="notify_janitor_on_ticket_creation" />
        <RichTextInput
          multiline
          rows={12}
          source="notify_janitor_on_ticket_creation_template"
        />
        <BooleanInput rows={6} source="notify_cs_on_ticket_creation" />
        <RichTextInput
          multiline
          rows={12}
          source="notify_cs_on_ticket_creation_template"
        />
      </SimpleForm>
    </Create>
  )
}

function arraysEqual(a1, a2) {
  /* WARNING: arrays must not contain {objects} or behavior may be undefined */
  return JSON.stringify(a1) == JSON.stringify(a2)
}
